// PRD-0019 — Share to corpus + Convert to brief on assistant agent messages

import { useState, useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAddToCorpus } from "../../hooks/useBrand";
import { useSurfaces } from "../../hooks/useSurfaces";
import {
  useCampaignList,
  useCampaignDetail,
  useCreateBrief,
  useCreateCampaignPacket,
  useSaveBriefDocument,
  useBriefDocument,
} from "../../hooks/useCampaigns";
import { ApiError } from "../../lib/api";
import type { Brief, BriefState } from "@helloconvo/shared";
import type { AgentArtifact } from "../../../../shared/types.ts";
import { isResearchLikeMessage } from "../../lib/research-output";
import { buildCampaignSurfaceOptions, pickDefaultCampaignSurface } from "../../lib/campaign-surfaces";

function apiErrorStatus(e: unknown): number | undefined {
  return e instanceof ApiError ? e.status : undefined;
}



interface SaveReceipt {
  artifactType: "corpus" | "brief" | "packet";
  title: string;
  destinationLabel: string;
  currentState: string;
  provenanceStatus: string;
  nextActionLabel: string;
  nextActionHref: string;
}

function artifactKeys(artifacts: AgentArtifact[] | null | undefined): string[] {
  return (artifacts ?? []).map((artifact) => artifact.key).filter((key) => key.trim().length > 0);
}

function artifactSummary(artifacts: AgentArtifact[] | null | undefined): string | null {
  const count = artifacts?.length ?? 0;
  if (count === 0) return null;
  return count === 1 ? "1 attached artifact" : `${count} attached artifacts`;
}

function briefStateLabel(state: BriefState): string {
  return state.replace(/_/g, " ");
}

function briefNextActionHref(brandId: string, campaignId: string, state: BriefState): string {
  if (state === "in_review" || state === "approved" || state === "scheduled") {
    return `/review?brandId=${encodeURIComponent(brandId)}`;
  }
  return `/brand/${brandId}/campaigns/${campaignId}`;
}

function briefNextActionLabel(state: BriefState): string {
  if (state === "in_review" || state === "approved" || state === "scheduled") return "Open review";
  return "Open campaign";
}

function saveProvenanceMetadata(messageId: string, artifacts: AgentArtifact[] | null | undefined): Record<string, unknown> {
  return {
    source: "agent_chat",
    agent_message_id: messageId,
    artifact_keys: artifactKeys(artifacts),
    artifact_count: artifacts?.length ?? 0,
  };
}

function formatBriefDocumentFromChat(raw: string, titleGuess: string): string {
  const t = titleGuess.slice(0, 120) || "Untitled brief";
  return [
    `## Brief: ${t}`,
    `**Platform**: _(set below or in campaign)_`,
    `**Format**: _e.g. long-form post_`,
    `**Audience**: _(who this is for)_`,
    `**Angle**: _(one sentence)_`,
    `**Key points** (3-5):`,
    `- `,
    `**Voice notes**: `,
    `**Source material**: agent chat`,
    ``,
    `---`,
    ``,
    raw.trim(),
  ].join("\n");
}

function defaultPacketTitle(raw: string): string {
  const heading = raw.match(/^\s{0,3}#{1,6}\s+(.+)$/m)?.[1]?.trim();
  if (heading) return heading.slice(0, 120);

  const firstLine = raw
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.length > 0);
  if (firstLine) {
    return firstLine.replace(/^[*-]\s*/, "").slice(0, 120);
  }

  return "Agent chat packet";
}

function MessageActionButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="group relative inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
    >
      {children}
      <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
        {label}
      </span>
    </button>
  );
}

export function AssistantMessageToolbar({
  brandId,
  messageId,
  content,
  artifacts = null,
}: {
  brandId: string;
  messageId: string;
  content: string;
  artifacts?: AgentArtifact[] | null;
}) {
  const addCorpus = useAddToCorpus(brandId);
  const [corpusConfirm, setCorpusConfirm] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [packetOpen, setPacketOpen] = useState(false);
  const [docBody, setDocBody] = useState(() => formatBriefDocumentFromChat(content, ""));
  const [campaignId, setCampaignId] = useState("");
  const [briefId, setBriefId] = useState<"new" | string>("new");
  const [newTitle, setNewTitle] = useState("");
  const [newPlatform, setNewPlatform] = useState("");
  const [newFormat, setNewFormat] = useState("");
  const surfacesQ = useSurfaces(brandId);
  const packetSurfaceOptions = buildCampaignSurfaceOptions(surfacesQ.data?.data ?? []);
  const defaultPacketSurface = pickDefaultCampaignSurface(surfacesQ.data?.data ?? []);
  const [packetCampaignId, setPacketCampaignId] = useState("");
  const [packetTitle, setPacketTitle] = useState("");
  const [packetSurface, setPacketSurface] = useState<Brief["surface"]>(defaultPacketSurface);
  const [packetStatus, setPacketStatus] = useState<"draft" | "pending_approval">("pending_approval");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveReceipt, setSaveReceipt] = useState<SaveReceipt | null>(null);
  const [corpusAdded, setCorpusAdded] = useState(false);
  const [corpusError, setCorpusError] = useState<string | null>(null);
  const [ackReplaceExisting, setAckReplaceExisting] = useState(false);

  const campListQ = useCampaignList(brandId);
  const campaigns = (campListQ.data?.data ?? []).filter((c) => c.status === "active" || c.status === "draft");

  const detailQ = useCampaignDetail(brandId, campaignId || undefined);
  const briefs = detailQ.data?.data?.briefs ?? [];

  const existingBriefSelected = briefId !== "new";
  const briefDocQ = useBriefDocument(
    campaignId || undefined,
    existingBriefSelected ? briefId : undefined,
    "draft",
    convertOpen && existingBriefSelected && !!campaignId,
  );

  const briefDocNotFound =
    briefDocQ.isError && apiErrorStatus(briefDocQ.error) === 404;
  const existingDocHasContent =
    briefDocQ.isSuccess && (briefDocQ.data?.data?.content?.trim().length ?? 0) > 0;
  const existingRevision = briefDocQ.data?.data?.revision;

  const campaignsQueryBlocked = campListQ.isPending || campListQ.isError;
  const detailQueryBlocked =
    !!campaignId && (detailQ.isPending || detailQ.isError);
  const briefDocQueryBlocked =
    existingBriefSelected &&
    (briefDocQ.isPending ||
      (briefDocQ.isError && !briefDocNotFound));
  const replaceNotAcknowledged =
    existingBriefSelected && existingDocHasContent && !ackReplaceExisting;

  const createBrief = useCreateBrief(brandId);
  const createPacket = useCreateCampaignPacket(packetCampaignId || undefined, brandId);
  const saveDoc = useSaveBriefDocument(brandId);
  const isResearchLike = isResearchLikeMessage({ content, artifacts });

  useEffect(() => {
    if (!corpusAdded) return;
    const id = window.setTimeout(() => setCorpusAdded(false), 5000);
    return () => window.clearTimeout(id);
  }, [corpusAdded]);

  useEffect(() => {
    if (packetSurfaceOptions.some((option) => option.value === packetSurface)) return;
    setPacketSurface(defaultPacketSurface);
  }, [defaultPacketSurface, packetSurface, packetSurfaceOptions]);

  async function handleShareCorpus() {
    setCorpusError(null);
    try {
      const metadata = saveProvenanceMetadata(messageId, artifacts);
      const result = await addCorpus.mutateAsync({ content, metadata });
      setCorpusConfirm(false);
      setCorpusAdded(true);
      setSaveReceipt({
        artifactType: "corpus",
        title: typeof result?.data?.id === "string" ? `Corpus entry ${result.data.id.slice(0, 8)}` : "Corpus entry",
        destinationLabel: "Brand corpus",
        currentState: "saved",
        provenanceStatus: artifactSummary(artifacts) ? `Agent chat source captured · ${artifactSummary(artifacts)}` : "Agent chat source captured",
        nextActionLabel: "Open corpus",
        nextActionHref: `/corpus?brandId=${encodeURIComponent(brandId)}`,
      });
    } catch (e) {
      setCorpusError(e instanceof Error ? e.message : "Failed to add to corpus");
    }
  }

  async function handleSaveBrief() {
    setSaveError(null);
    if (!campaignId) {
      setSaveError("Select a campaign.");
      return;
    }
    if (campaignsQueryBlocked) {
      setSaveError("Campaigns could not be loaded. Fix errors above or retry.");
      return;
    }
    if (campaignId && detailQueryBlocked) {
      setSaveError("Brief list could not be loaded. Fix errors above or retry.");
      return;
    }
    if (existingBriefSelected && briefDocQueryBlocked) {
      setSaveError("Could not verify the current brief document. Retry or pick another brief.");
      return;
    }
    if (replaceNotAcknowledged) {
      setSaveError("Confirm that you want to replace the existing brief document.");
      return;
    }
    const wasCreatingNewBrief = briefId === "new";
    try {
      let targetBriefId = briefId;
      if (briefId === "new") {
        if (!newTitle.trim()) {
          setSaveError("Title is required for a new brief.");
          return;
        }
        const created = await createBrief.mutateAsync({
          campaignId,
          title: newTitle.trim(),
          platform: newPlatform.trim() || null,
          format: newFormat.trim() || null,
        });
        targetBriefId = (created as { data: Brief }).data.id;
        setBriefId(targetBriefId);
      }
      if (targetBriefId === "new" || !targetBriefId) {
        setSaveError("Could not resolve brief.");
        return;
      }
      await saveDoc.mutateAsync({
        campaignId,
        briefId: targetBriefId,
        type: "draft",
        content: docBody,
        metadata: saveProvenanceMetadata(messageId, artifacts),
      });
      const campTitle = campaigns.find((c) => c.id === campaignId)?.title ?? "Campaign";
      const selectedBrief = briefs.find((b) => b.id === targetBriefId);
      const briefTitle = wasCreatingNewBrief
        ? newTitle.trim()
        : selectedBrief?.title ?? "Brief";
      const briefState = wasCreatingNewBrief ? "ideation" : (selectedBrief?.state ?? "ideation");
      setSaveReceipt({
        artifactType: "brief",
        title: briefTitle,
        destinationLabel: `${campTitle} · ${targetBriefId}` ,
        currentState: briefStateLabel(briefState),
        provenanceStatus: artifactSummary(artifacts) ? `Draft document saved with agent-chat provenance · ${artifactSummary(artifacts)}` : "Draft document saved with agent-chat provenance",
        nextActionLabel: briefNextActionLabel(briefState),
        nextActionHref: briefNextActionHref(brandId, campaignId, briefState),
      });
      setConvertOpen(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Save failed");
    }
  }

  async function handleSavePacket() {
    setSaveError(null);
    if (!packetCampaignId) {
      setSaveError("Select a campaign.");
      return;
    }
    if (campaignsQueryBlocked) {
      setSaveError("Campaigns could not be loaded. Fix errors above or retry.");
      return;
    }
    if (!packetTitle.trim()) {
      setSaveError("Title is required for a packet.");
      return;
    }
    try {
      const result = await createPacket.mutateAsync({
        campaignId: packetCampaignId,
        title: packetTitle.trim(),
        draft_body: content.trim(),
        surface: packetSurface ?? defaultPacketSurface,
        status: packetStatus,
        metadata: saveProvenanceMetadata(messageId, artifacts),
      });
      const packet = result.data;
      const campTitle = campaigns.find((c) => c.id === packetCampaignId)?.title ?? "Campaign";
      setSaveReceipt({
        artifactType: "packet",
        title: packet.title,
        destinationLabel: `${campTitle} · ${packet.brief_id}`,
        currentState: packet.status.replace(/_/g, " "),
        provenanceStatus: artifactSummary(artifacts) ? `Packet saved with agent-chat provenance · ${artifactSummary(artifacts)}` : "Packet saved with agent-chat provenance",
        nextActionLabel: packet.status === "pending_approval" || packet.status === "approved" || packet.status === "scheduled" ? "Open review" : "Open campaign",
        nextActionHref: packet.status === "pending_approval" || packet.status === "approved" || packet.status === "scheduled"
          ? `/review?brandId=${encodeURIComponent(brandId)}`
          : `/brand/${brandId}/campaigns/${packetCampaignId}`,
      });
      setPacketOpen(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Save failed");
    }
  }

  return (
    <>
      <div
        data-testid={isResearchLike ? "research-actions-toolbar" : "message-actions-toolbar"}
        className="mt-3 flex items-center gap-2 border-t border-slate-200 pt-3"
      >
        <MessageActionButton
          label="Share to corpus"
          onClick={() => {
            setCorpusError(null);
            setCorpusConfirm(true);
          }}
        >
          <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <ellipse cx="12" cy="5" rx="7" ry="3" />
            <path d="M5 5v6c0 1.7 3.1 3 7 3s7-1.3 7-3V5M5 11v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
          </svg>
        </MessageActionButton>
        <MessageActionButton
          label="Convert to brief"
          onClick={() => {
            setDocBody(formatBriefDocumentFromChat(content, ""));
            setSaveError(null);
            setSaveReceipt(null);
            setCampaignId("");
            setBriefId("new");
            setNewTitle("");
            setNewPlatform("");
            setNewFormat("");
            setAckReplaceExisting(false);
            setConvertOpen(true);
          }}
        >
          <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 3.75h7l3 3V20.25H7zM14 3.75v3h3M9.5 11h5M9.5 14.5h5" />
          </svg>
        </MessageActionButton>

        <MessageActionButton
          label="Save as packet"
          onClick={() => {
            setSaveError(null);
            setSaveReceipt(null);
            setPacketCampaignId("");
            setPacketTitle(defaultPacketTitle(content));
            setPacketSurface(defaultPacketSurface);
            setPacketStatus("pending_approval");
            setPacketOpen(true);
          }}
        >
          <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.75 7.25h14.5v12H4.75zM7.25 4.75h9.5v2.5M9 11h6M9 14.5h4" />
          </svg>
        </MessageActionButton>
      </div>

      {corpusConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="corpus-confirm-title"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
        >
          <div className="w-full max-w-sm border border-slate-200 bg-white p-5 shadow-xl">
            <h2 id="corpus-confirm-title" className="text-base font-semibold text-slate-900">
              Add this to your brand corpus?
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              The full assistant message will be stored as corpus text for retrieval.
            </p>
            {corpusError && (
              <p className="mt-3 text-sm text-red-700" role="alert">
                {corpusError}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setCorpusConfirm(false);
                  setCorpusError(null);
                }}
                className="border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={addCorpus.isPending}
                onClick={() => {
                  void handleShareCorpus();
                }}
                className="bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {addCorpus.isPending ? "Adding…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {convertOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="convert-brief-dialog-title"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
        >
          <div className="flex max-h-[90vh] w-full max-w-lg flex-col border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-200 px-4 py-3">
              <h2 id="convert-brief-dialog-title" className="text-lg font-semibold text-slate-900">
                Save to brief document
              </h2>
              <p className="text-sm text-slate-500">Edit the structured brief below, then pick a campaign and brief.</p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 space-y-3">
              <label className="block text-xs font-medium text-slate-600">
                Document
                <textarea
                  value={docBody}
                  onChange={(e) => setDocBody(e.target.value)}
                  rows={12}
                  className="mt-1 w-full border border-slate-300 px-2 py-2 font-mono text-xs text-slate-800"
                />
              </label>
              {campListQ.isPending && (
                <p className="text-xs text-slate-500" role="status">
                  Loading campaigns…
                </p>
              )}
              {campListQ.isError && (
                <p className="text-sm text-red-700" role="alert">
                  {campListQ.error instanceof Error ? campListQ.error.message : "Failed to load campaigns."}{" "}
                  <button
                    type="button"
                    className="font-medium underline"
                    onClick={() => void campListQ.refetch()}
                  >
                    Retry
                  </button>
                </p>
              )}
              <label className="block text-xs font-medium text-slate-600">
                Campaign
                <select
                  value={campaignId}
                  disabled={campaignsQueryBlocked}
                  onChange={(e) => {
                    setCampaignId(e.target.value);
                    setBriefId("new");
                    setAckReplaceExisting(false);
                  }}
                  className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <option value="">— Select —</option>
                  {campaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </label>
              {campaignId && (
                <>
                  {detailQ.isPending && (
                    <p className="text-xs text-slate-500" role="status">
                      Loading briefs…
                    </p>
                  )}
                  {detailQ.isError && (
                    <p className="text-sm text-red-700" role="alert">
                      {detailQ.error instanceof Error ? detailQ.error.message : "Failed to load briefs."}{" "}
                      <button
                        type="button"
                        className="font-medium underline"
                        onClick={() => void detailQ.refetch()}
                      >
                        Retry
                      </button>
                    </p>
                  )}
                  <label className="block text-xs font-medium text-slate-600">
                    Brief
                    <select
                      value={briefId}
                      disabled={detailQueryBlocked}
                      onChange={(e) => {
                        setBriefId(e.target.value === "new" ? "new" : e.target.value);
                        setAckReplaceExisting(false);
                      }}
                      className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <option value="new">Create new brief…</option>
                      {briefs.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  {existingBriefSelected && briefDocQ.isPending && (
                    <p className="text-xs text-slate-500" role="status">
                      Loading current brief document…
                    </p>
                  )}
                  {existingBriefSelected && briefDocQ.isError && !briefDocNotFound && (
                    <p className="text-sm text-red-700" role="alert">
                      {briefDocQ.error instanceof Error ? briefDocQ.error.message : "Could not load brief document."}{" "}
                      <button
                        type="button"
                        className="font-medium underline"
                        onClick={() => void briefDocQ.refetch()}
                      >
                        Retry
                      </button>
                    </p>
                  )}
                  {existingBriefSelected && existingDocHasContent && (
                    <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                      <p className="font-medium">Replace existing draft?</p>
                      <p className="mt-1 text-amber-900">
                        This brief already has a saved draft
                        {existingRevision != null ? ` (revision ${existingRevision})` : ""}. Saving will overwrite it
                        with the document in the editor above.
                      </p>
                      <label className="mt-2 flex cursor-pointer items-start gap-2">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={ackReplaceExisting}
                          onChange={(e) => setAckReplaceExisting(e.target.checked)}
                        />
                        <span>I understand this replaces the existing draft</span>
                      </label>
                    </div>
                  )}
                  {briefId === "new" && (
                    <div className="space-y-2 border border-dashed border-slate-200 p-3">
                      <label htmlFor="new-brief-title" className="block text-xs font-medium text-slate-600">
                        Brief title
                        <input
                          id="new-brief-title"
                          type="text"
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          autoComplete="off"
                          className="mt-1 w-full border border-slate-300 px-2 py-1.5 text-sm"
                        />
                      </label>
                      <label htmlFor="new-brief-platform" className="block text-xs font-medium text-slate-600">
                        Platform <span className="font-normal text-slate-500">(optional)</span>
                        <input
                          id="new-brief-platform"
                          type="text"
                          value={newPlatform}
                          onChange={(e) => setNewPlatform(e.target.value)}
                          autoComplete="off"
                          className="mt-1 w-full border border-slate-300 px-2 py-1.5 text-sm"
                        />
                      </label>
                      <label htmlFor="new-brief-format" className="block text-xs font-medium text-slate-600">
                        Format <span className="font-normal text-slate-500">(optional)</span>
                        <input
                          id="new-brief-format"
                          type="text"
                          value={newFormat}
                          onChange={(e) => setNewFormat(e.target.value)}
                          autoComplete="off"
                          className="mt-1 w-full border border-slate-300 px-2 py-1.5 text-sm"
                        />
                      </label>
                    </div>
                  )}
                </>
              )}
              {saveError && (
                <p className="text-sm text-red-700" role="alert">
                  {saveError}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
              <button
                type="button"
                onClick={() => setConvertOpen(false)}
                className="border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  saveDoc.isPending ||
                  createBrief.isPending ||
                  campaignsQueryBlocked ||
                  detailQueryBlocked ||
                  briefDocQueryBlocked ||
                  replaceNotAcknowledged
                }
                onClick={() => void handleSaveBrief()}
                className="bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saveDoc.isPending || createBrief.isPending ? "Saving…" : "Save to brief"}
              </button>
            </div>
          </div>
        </div>
      )}


      {packetOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="save-packet-dialog-title"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
        >
          <div className="flex max-h-[90vh] w-full max-w-lg flex-col border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-200 px-4 py-3">
              <h2 id="save-packet-dialog-title" className="text-lg font-semibold text-slate-900">
                Save as content packet
              </h2>
              <p className="text-sm text-slate-500">Store this assistant output as a packet-backed brief with a visible review destination.</p>
            </div>
            <div className="space-y-3 overflow-y-auto px-4 py-3">
              <label className="block text-xs font-medium text-slate-600">
                Campaign
                <select
                  value={packetCampaignId}
                  disabled={campaignsQueryBlocked}
                  onChange={(e) => setPacketCampaignId(e.target.value)}
                  className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <option value="">— Select —</option>
                  {campaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </label>
              <label htmlFor="new-packet-title" className="block text-xs font-medium text-slate-600">
                Title
                <input
                  id="new-packet-title"
                  type="text"
                  value={packetTitle}
                  onChange={(e) => setPacketTitle(e.target.value)}
                  autoComplete="off"
                  className="mt-1 w-full border border-slate-300 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block text-xs font-medium text-slate-600">
                Surface
                <select
                  value={packetSurface ?? defaultPacketSurface}
                  onChange={(e) => setPacketSurface(e.target.value as Brief["surface"])}
                  className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"
                >
                  {packetSurfaceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs font-medium text-slate-600">
                Packet status
                <select
                  value={packetStatus}
                  onChange={(e) => setPacketStatus(e.target.value as "draft" | "pending_approval")}
                  className="mt-1 w-full border border-slate-300 px-2 py-2 text-sm"
                >
                  <option value="pending_approval">Pending approval</option>
                  <option value="draft">Draft</option>
                </select>
              </label>
              <label className="block text-xs font-medium text-slate-600">
                Draft body
                <textarea
                  value={content}
                  readOnly
                  rows={10}
                  className="mt-1 w-full border border-slate-300 bg-slate-50 px-2 py-2 text-xs text-slate-800"
                />
              </label>
              {saveError && (
                <p className="text-sm text-red-700" role="alert">
                  {saveError}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
              <button
                type="button"
                onClick={() => setPacketOpen(false)}
                className="border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={createPacket.isPending || campaignsQueryBlocked || !packetCampaignId || !packetTitle.trim()}
                onClick={() => void handleSavePacket()}
                className="bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {createPacket.isPending ? "Saving…" : "Create packet"}
              </button>
            </div>
          </div>
        </div>
      )}

      {saveReceipt && (
        <div
          id="agent-save-receipt"
          tabIndex={-1}
          role="status"
          className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-3 text-xs text-emerald-950"
        >
          <p className="font-semibold uppercase tracking-wide text-emerald-800">Saved {saveReceipt.artifactType}</p>
          <p className="mt-1 text-sm font-semibold text-emerald-950">{saveReceipt.title}</p>
          <div className="mt-2 grid gap-1 text-xs text-emerald-900">
            <p><span className="font-medium">Destination:</span> {saveReceipt.destinationLabel}</p>
            <p><span className="font-medium">Current state:</span> {saveReceipt.currentState}</p>
            <p><span className="font-medium">Provenance:</span> {saveReceipt.provenanceStatus}</p>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Link
              to={saveReceipt.nextActionHref}
              className="font-medium text-blue-700 underline"
            >
              {saveReceipt.nextActionLabel}
            </Link>
            <button
              type="button"
              onClick={() => setSaveReceipt(null)}
              className="text-emerald-800 underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {corpusAdded && (
        <p className="mt-1 text-xs text-emerald-700" role="status">
          Added to corpus.
        </p>
      )}
    </>
  );
}
